import React, { useState, useEffect, useCallback } from 'react'

import { toast } from 'react-toastify'

import Result from '../../../../../presentation/pages/contractors/result'

import { useResource } from '../../../../../presentation/hooks/resource'
import { useFetchResources } from '../../../../../presentation/hooks/fetch-resources'
import { useFilter } from '../../../../../presentation/contexts/filter/filter'
import { useMe } from '../../../../../presentation/contexts/me'

import { makeSpecialties } from '../../../../usecases/specialties/remote-specialties-factory'
import { makeSearchDoctors } from '../../../../usecases/search-doctors/remote-search-doctors-factory'
import { makeCities } from '../../../../usecases/cities/remote-cities-factory'
import { makeStates } from '../../../../usecases/states/remote-states-factory'

import ISearchState from '../../../../../presentation/interfaces/search-state'
import ISpecialty from '../../../../../presentation/interfaces/specialty'
import IState from '../../../../../presentation/interfaces/state'
import ILocation from '../../../../../presentation/interfaces/location'

import HelperCitySearch from '../../../../../presentation/helpers/helper-search-city'
import isHttpError from '../../../../../presentation/helpers/helper-http-error'
import { formatAvailability, formatLocations } from './utils'

const MakeResult = () => {
  document.title = 'BuscarMédicos - Resultados'

  const [filterVisibility, setFilterVisibility] = useState(false)

  const [searchResults, setSearchResults] = useState([])
  const [searchRelatedResults, setSearchRelatedResults] = useState([])

  const { filter } = useFilter()

  const { me } = useMe()

  const [specialties, specialtiesService] = useResource<ISpecialty>(
    makeSpecialties(),
  )

  const [states, setStates] = useState<IState[]>([])

  useFetchResources(specialtiesService.load)

  const fetchResult = useCallback(
    async (params: ISearchState) => {
      if (me) {
        try {
          const payload = {
            specialties: params.specialties,
            availability: formatAvailability(params.availability),
            locations: formatLocations(params.locations),
          }
          const resultsData = await makeSearchDoctors(me?.id).post(payload)
          setSearchResults(resultsData)

          let relatedResultsLocations: ILocation[] = []

          const promises = params.locations.map(async (location) => {
            const cities = await HelperCitySearch.getCitiesWithinDistance(
              location.cities,
            )
            relatedResultsLocations.push({ state: location.state, cities })
          })

          await Promise.all(promises)

          relatedResultsLocations = relatedResultsLocations.map((location) => {
            const uniqueCities = Array.from(new Set(location.cities))
            const filteredCities = uniqueCities.filter((city) => {
              const paramLocation = params.locations.find(
                (loc) => loc.state === location.state,
              )
              return paramLocation ? !paramLocation.cities.includes(city) : true
            })

            return {
              state: location.state,
              cities: filteredCities,
            }
          })

          relatedResultsLocations = relatedResultsLocations.filter(
            (location) => location.cities.length > 0,
          )

          if (
            relatedResultsLocations.map((location) => location.cities).flat()
              .length === 0
          ) return

          const relatedResultsPayload = {
            specialties: params.specialties,
            availability: formatAvailability(params.availability),
            locations: formatLocations(relatedResultsLocations),
          }

          const relatedResultsData = await makeSearchDoctors(me?.id).post(
            relatedResultsPayload,
          )

          setSearchRelatedResults(relatedResultsData)
        } catch (error) {
          const httpError = isHttpError(error)

          if (httpError) {
            toast.error(httpError.message)
          } else {
            throw error
          }
        }
      }
    },
    [filter, me?.id],
  )

  useEffect(() => {
    fetchResult(filter)
  }, [])

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const data = await makeStates().load()
        const formattedData = data.map((state: any) => ({
          ...state,
          siglaIsoIntegration: state.siglaIsoIntegration.slice(3, 5),
        }))

        setStates(formattedData)
      } catch (error) {
        const httpError = isHttpError(error)
        if (httpError) {
          toast.error(`Erro ao buscar os estados: ${httpError.message}`)
        } else {
          throw error
        }
      }
    }

    fetchStates()
  }, [])

  const loadCities = async (stateId: number) => {
    const cities = makeCities()
    return cities.loadById(stateId)
  }

  const toggleFilterVisibility = () => {
    setFilterVisibility(!filterVisibility)
  }

  const handleSearch = async (values: ISearchState) => {
    setFilterVisibility(false)
    fetchResult(values)
  }

  const results = searchResults.map((doctor: any) => ({
    ...doctor,
    location: doctor.cities.map((city: any) => city.name).join(', '),
    linkChat: `/chat/MEDICO/${doctor.id}`,
    linkProfile: `/contractors/doctor/${doctor.id}`,
  }))

  const relatedResults = searchRelatedResults.map((doctor: any) => ({
    ...doctor,
    location: doctor.cities.map((city: any) => city.name).join(', '),
    linkChat: `/chat/MEDICO/${doctor.id}`,
    linkProfile: `/contractors/doctor/${doctor.id}`,
  }))

  const resultProps = {
    filter,
    filterVisibility,
    toggleFilterVisibility,
    handleSearch,
    results,
    relatedResults,
    specialties,
    states,
    loadCities,
  }

  return <Result {...resultProps} />
}

export default MakeResult
